<template>
  <InfuseModal :title="t('e_connector.chargingStationLinks.modal.update.title')" @close="emit('close')">
    <form ref="form" id="create-link" @submit.prevent="onSave()">
      <div class="flex max-h-[475px] w-[600px] flex-col gap-4 overflow-y-auto px-5">
        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.common.chargingStationLink.centralSystem')"
            class="basis-1/2"
            size="small"
          >
            <InfuseSelect
              name="centralSystem"
              size="small"
              v-model="linkedCentralSystemData.centralSystemId"
              :options="centralSystemOptions"
              :label="t('e_connector.chargingStationLinks.modal.field.centralSystem.placeholder')"
              :required="true"
              disabled
            />
          </InfuseField>

          <InfuseField :label="t('e_connector.common.chargingStationLink.type')" class="basis-1/2" size="small">
            <template v-if="!mayCreateMaster" #label>
              <InfuseTooltip
                :content="t('e_connector.chargingStationLinks.modal.field.type.tooltip')"
                class="inline cursor-help text-yellow-500"
              >
                <font-awesome-icon :icon="['fal', 'circle-exclamation']" />
              </InfuseTooltip>
            </template>

            <InfuseSelect
              name="type"
              size="small"
              v-model="linkedCentralSystemData.type"
              :options="typeOptions"
              :required="true"
              :disabled="!mayCreateMaster"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.common.chargingStationLink.customChargingStationName')"
            class="basis-full"
            size="small"
            optional
          >
            <InfuseInput
              size="small"
              v-model="linkedCentralSystemData.customChargingStationName"
              :maxlength="255"
              :placeholder="t('e_connector.chargingStationLinks.modal.field.customChargingStationName.placeholder')"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.common.chargingStationLink.commandsAllowed')"
            class="basis-1/3"
            size="small"
          >
            <InfuseToggle
              id="commandsAllowed"
              :value="linkedCentralSystemData.commandsAllowed"
              @on="linkedCentralSystemData.commandsAllowed = true"
              @off="linkedCentralSystemData.commandsAllowed = false"
            />
          </InfuseField>

          <InfuseField
            :label="t('e_connector.common.chargingStationLink.authorizationAllowed')"
            class="basis-1/3"
            size="small"
          >
            <InfuseToggle
              id="sessionAuthorizationAllowed"
              :value="linkedCentralSystemData.sessionAuthorizationAllowed"
              @on="linkedCentralSystemData.sessionAuthorizationAllowed = true"
              @off="linkedCentralSystemData.sessionAuthorizationAllowed = false"
            />
          </InfuseField>

          <InfuseField
            :label="t('e_connector.common.chargingStationLink.forwardBasicAuth')"
            class="basis-1/3"
            size="small"
          >
            <InfuseToggle
              id="forwardBasicAuth"
              :value="linkedCentralSystemData.forwardBasicAuth"
              @on="linkedCentralSystemData.forwardBasicAuth = true"
              @off="linkedCentralSystemData.forwardBasicAuth = false"
            />
          </InfuseField>
        </div>
      </div>
    </form>

    <template #actions>
      <InfuseButton data-testid="modal-cancel-button" color="secondary" @click="emit('close')">
        {{ t('e_connector.common.action.cancel') }}
      </InfuseButton>
      <InfuseButton
        data-testid="modal-confirm-button"
        type="submit"
        form="create-link"
        :disabled="loading"
        :loading="loading"
      >
        {{ t('e_connector.common.action.update') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import {
  InfuseButton,
  InfuseField,
  InfuseInput,
  InfuseModal,
  InfuseSelect,
  InfuseToggle,
  InfuseTooltip,
} from '@infuse/ui/components';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { CentralSystem, LinkedCentralSystem, LinkedCentralSystemType } from '@infuse/e-connector/central-system/domain';
import { ChargingStation } from '@infuse/e-connector/charging-station/domain';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

interface Props {
  loading: boolean;
  linkedCentralSystem: LinkedCentralSystem;
  centralSystems: CentralSystem[];
  chargingStation: ChargingStation;
}

interface Emits {
  (e: 'close'): void;
  (e: 'update', linkedCentralSystem: LinkedCentralSystem): void;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
});

const emit = defineEmits<Emits>();

const { t } = useI18n();

const linkedCentralSystemData = ref<LinkedCentralSystem>({ ...props.linkedCentralSystem }); // shallow copy

const centralSystemOptions = computed(() =>
  props.centralSystems
    .filter((centralSystem) =>
      props.chargingStation.linkedCentralSystems.every((lcs) => lcs.centralSystemId !== centralSystem.id)
    )
    .map((centralSystem) => ({
      key: centralSystem.name,
      value: centralSystem.id
    }))
);
const typeOptions = Object.values(LinkedCentralSystemType);

const mayCreateMaster = computed(
  () =>
    props.linkedCentralSystem.type === LinkedCentralSystemType.MASTER ||
    !props.chargingStation.linkedCentralSystems.some(
      (linkedCentralSystem) => linkedCentralSystem.type === LinkedCentralSystemType.MASTER
    )
);

function onSave() {
  emit('update', linkedCentralSystemData.value as LinkedCentralSystem);
}
</script>
